import { useState } from 'react';
import { AlertCircle, Check, ChevronDown, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { JobFileUploadItem } from '@/hooks/useJobFiles';
import { JobFileUploadDetails, summarizeUploads } from './JobFileUploadProgress';

// The mobile face of a running batch: one line docked above the capture bar,
// fixed so it survives the scroll through the grid. A 20-photo batch's per-file
// list would swallow a 390px screen, and while shooting the only question is
// "how many are through" — so the detail lives one tap down.

interface JobFileUploadProgressBarProps {
  items: JobFileUploadItem[];
  onRetry: (id: string) => void;
  onRetryAll: () => void;
}

export function JobFileUploadProgressBar({
  items,
  onRetry,
  onRetryAll,
}: JobFileUploadProgressBarProps) {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  const summary = summarizeUploads(items);
  const failedCount = summary.failed.length + summary.originalFailed.length;
  const busy = summary.inFlight.length > 0;

  return (
    // bottom-[calc(...)] rather than stacking in flow: the capture bar underneath
    // is itself fixed, and this has to sit exactly on top of it whatever the
    // device's safe-area inset turns out to be.
    <div
      className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+3.75rem)] z-30 border-t border-gray-200 bg-white sm:hidden"
      aria-live="polite"
    >
      {expanded && (
        <div className="max-h-[40vh] space-y-2 overflow-y-auto border-b border-gray-100 p-3">
          <JobFileUploadDetails summary={summary} onRetry={onRetry} />
          {summary.hasFailures && (
            <Button variant="outline" size="sm" className="w-full" onClick={onRetryAll}>
              Retry all failed
            </Button>
          )}
        </div>
      )}

      {/* The whole row is the target — a chevron alone is too small to hit. */}
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
        className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />
        ) : failedCount > 0 ? (
          <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
        ) : (
          <Check className="h-4 w-4 shrink-0 text-green-600" />
        )}
        <span className="text-sm font-medium text-gray-700">
          {summary.uploaded} of {summary.total} uploaded
        </span>
        {failedCount > 0 && (
          <span className="text-sm font-medium text-red-600">{failedCount} failed</span>
        )}
        <ChevronDown
          className={`ml-auto h-4 w-4 shrink-0 text-gray-400 transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>
    </div>
  );
}
